"use client";

import { useMediaQuery } from "@/app/directories/companion/(main)/roster/hooks/useMediaQuery";
import {
  getCategories,
  getProductsByCategoryId,
} from "@frontend/lib/actions/actions";
import { getOrganizationAssetsForOrg } from "@frontend/lib/actions/config-actions";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { usePathname } from "next/navigation";
import { ProductCardCarousel } from "../productCardCarousel/ProductCardCarousel";
import { FeaturedCarousel } from "./FeaturedCarousel";

interface FeaturedCarouselWrapperClientProps {
  params: { clientSlug: string; orgId: number; kioskMode?: boolean };
  props: {
    scrollId: string;
    isMinimal: boolean;
    primaryActionText: string;
    categoryDisplayName?: string;
    showIcon?: boolean;
    iconURL?: string;
  };
}

export function FeaturedCarouselWrapperClient({
  params,
  props,
}: FeaturedCarouselWrapperClientProps) {
  const pathname = usePathname();
  const isMobile =
    pathname === `/${params.clientSlug}`
      ? useMediaQuery("(max-width: 1024px)")
      : useMediaQuery("(max-width: 768px)");

  // Fetch categories
  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ["categories", params.orgId],
    queryFn: () => getCategories(params.orgId),
    staleTime: 5 * 60 * 1000,
    select: (data) =>
      data
        .sort((a, b) => b.popularity - a.popularity)
        .filter((category) => category.name === "Featured"),
  });

  // Fetch products based on categories
  const { data: products, isLoading: productsLoading } = useQuery({
    queryKey: ["products", categories?.map((c) => c.id)],
    queryFn: () =>
      getProductsByCategoryId(categories?.map((category) => category.id) || []),
    enabled: !!categories?.length,
    staleTime: 5 * 60 * 1000,
    select: (data) => data.sort((a, b) => b.popularity - a.popularity),
  });

  const { data: organizationAssets } = useQuery({
    queryKey: ["organizationAssets", params.orgId],
    queryFn: () => getOrganizationAssetsForOrg(params.orgId),
    staleTime: 5 * 60 * 1000,
  });

  // Loading state
  if (categoriesLoading || productsLoading) {
    return (
      <div className="w-full max-w-[1200px] mx-auto py-12 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // No categories or products
  if (!categories?.length || !products?.length) {
    return null;
  }

  return isMobile ? (
    <ProductCardCarousel
      products={products}
      clientSlug={params.clientSlug}
      categoryName={"Featured"}
      isMinimal={props.isMinimal}
      orgId={params.orgId}
      showIcon={props.showIcon}
      iconURL={props.iconURL}
      kioskMode={!!params.kioskMode}
      primaryColor={organizationAssets?.primaryColor}
      primaryBackgroundColor={organizationAssets?.primaryBackgroundColor}
      secondaryBackgroundColor={organizationAssets?.secondaryBackgroundColor}
    />
  ) : (
    <FeaturedCarousel
      products={products}
      clientSlug={params.clientSlug}
      scrollId={props.scrollId}
      isMinimal={props.isMinimal}
      orgId={params.orgId}
      primaryActionText={props.primaryActionText}
      showIcon={props.showIcon}
      iconURL={props.iconURL}
      categoryDisplayName={props.categoryDisplayName}
      kioskMode={!!params.kioskMode}
    />
  );
}
